
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const StudyPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    wechat: '',
    education: '',
    problems: ''
  });

  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'problems') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.wechat) {
      alert('请填写必填项');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('wechat', formData.wechat);
      submitData.append('education', formData.education);
      submitData.append('problems', formData.problems);

      const response = await fetch('https://readdy.ai/api/form/d450mgts1qnclj70j6jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          wechat: '',
          education: '',
          problems: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToAssessment = () => {
    const assessmentSection = document.getElementById('assessment-section');
    if (assessmentSection) {
      assessmentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - 缩小高度到原来的三分之一 */}
      <section 
        className="relative pt-20 pb-12 px-6 flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/644c8f4a90e6d8d5a177b847eb82b944.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '50vh'
        }}
      >
        <div className="max-w-6xl mx-auto text-center text-white relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            留学申请
          </h1>
          <p className="text-lg md:text-xl mb-8 leading-relaxed max-w-4xl mx-auto">
            以更好职业前景为目标的留学规划。专业团队助您进入世界顶尖学府，开启国际化职业生涯
          </p>
          <button
            onClick={scrollToAssessment}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            立即咨询留学服务
          </button>
        </div>
      </section>

      {/* 三大核心优势 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">三大核心优势</h2>
            <p className="text-xl text-gray-600">资源、科技、服务三位一体，为您打造最专业的留学申请服务</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 顶级教育资源 */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-graduation-cap-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">顶级教育资源（资源优势）</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                我们拥有业内最丰富的教育资源网络，包括名校招生官、教授科研项目、专业文书团队、考试培训专家，为您的留学之路提供全方位支持。
              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-blue-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">直面招生官，解析内部标准</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-blue-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">13位院士+217名教授，携手前沿科研</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-blue-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">出题官+考官，原题预测+教学</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-blue-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">文书导师4对1，以专业性和差异性重新定义留学</span>
                </div>
              </div>
            </div>

            {/* AI智能规划 */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-cpu-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI智能规划（科技优势）</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                嵌入千所名校未公布真实就业数据，为更好前程精准铺路。
              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-purple-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">毕业后全球职业发展规划</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-purple-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">全球名校拒录大数据分析</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-purple-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">全球生源竞争力实时对比</span>
                </div>
              </div>
            </div>

            {/* 13年服务经验 */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-award-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">13年服务经验（服务优势）</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                无论您面临什么样的挑战，我们都有成功的解决方案和丰富的成功案例。
              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-green-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">擅长处理升学问题</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-green-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">全流程一站式服务</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-green-600 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm">个性化服务提升</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 资源优势 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">资源优势</h2>
            <p className="text-xl text-gray-600">四大核心资源，为您的留学申请保驾护航</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 直面招生官 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="relative mb-6">
                <img 
                  src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/bdcfd7ecdc0e2796fa6f04d3f6918bfa.png" 
                  alt="直面招生官" 
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  招生官资源
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">直面招生官，洞察梦校偏好</h3>
              <p className="text-gray-600 text-sm mb-4">
                186名顶尖学府招生官为你解密内部标准，查缺补漏事半功倍（部分展示）
              </p>
            </div>

            {/* 院士教授科研 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="relative mb-6">
                <img 
                  src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/7eb575bd7eae75791e331cf18f033420.png" 
                  alt="院士教授科研" 
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  学术资源
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">13位院士+217名教授，携手前沿科研</h3>
              <p className="text-gray-600 text-sm mb-4">
                13名海内外院士，216名大学教授指导前沿科研项目，帮助学生提升学术背景（部分展示）
              </p>
            </div>

            {/* 出题官考官 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="relative mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/a17d8fa81ed1f821db96a71432a80d90.jpg" 
                  alt="出题官考官" 
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  考试资源
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">出题官+考官，原题预测+教学</h3>
              <p className="text-gray-600 text-sm mb-4">
                携手托福/雅思/GRE/GMAT出题官、考官全面预测原题，深度细致教学，助你高效出分
              </p>
            </div>

            {/* 文书导师 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="relative mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20writing%20team%20of%20international%20essay%20tutors%20working%20collaboratively%20on%20university%20application%20documents%2C%20modern%20office%20environment%20with%20diverse%20academic%20advisors%20reviewing%20student%20essays%20and%20personal%20statements&width=400&height=250&seq=writing-tutors-1&orientation=landscape" 
                  alt="文书导师" 
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  文书资源
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">文书导师4对1，以专业性和差异性重新定义留学</h3>
              <p className="text-gray-600 text-sm mb-4">
                主导导师/学术导师/职业导师/外籍文书导师四对一私人服务团队
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 科技优势 - 三大数据洞察 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">科技优势</h2>
            <p className="text-xl text-gray-600">基于海量数据的深度分析，为您的申请保驾护航</p>
          </div>
          
          <div className="space-y-16">
            {/* 全球名校拒录大数据 */}
            <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">全球名校拒录大数据</h3>
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed">分析100万+申请案例，识别拒录原因和规律</p>
                  <div className="bg-white rounded-xl p-6 mb-8">
                    <div className="text-2xl font-bold text-purple-600 mb-2">1,000,000+ 申请案例分析</div>
                    <div className="text-gray-600">数据规模</div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">核心发现：</h4>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">标化成绩不是唯一决定因素</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">目标校越好越重视软实力背景</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">科研成果可将录取率提升数十倍</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">强力推荐信的真实影响力</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <img 
                      src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/95300de8df9bc0b78092c2c8a99c8619.png" 
                      alt="全球名校拒录大数据" 
                      className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 全球生源学校竞争力数据对比 */}
            <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="lg:order-2">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">全球生源学校竞争力数据对比</h3>
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed">为学生实时对比海内外各校竞争者差距</p>
                  <div className="bg-white rounded-xl p-6 mb-8">
                    <div className="text-2xl font-bold text-purple-600 mb-2">5,000+ 所海内外院校数据库</div>
                    <div className="text-gray-600">数据规模</div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">核心发现：</h4>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">升学最大分水岭在高二/大二/研一</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">平均分竟不是升学最大影响因素</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">科研成果可将录取率提升数十倍</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">强力推荐信的真实影响力</span>
                    </div>
                  </div>
                </div>
                <div className="lg:order-1">
                  <div className="relative">
                    <img 
                      src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/429c1ec113c610daa1eafddadce652a8.png" 
                      alt="全球生源学校竞争力数据对比" 
                      className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 毕业后职业发展大数据 */}
            <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">毕业后职业发展大数据</h3>
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed">追踪分析毕业生就业情况，指导专业选择</p>
                  <div className="bg-white rounded-xl p-6 mb-8">
                    <div className="text-2xl font-bold text-purple-600 mb-2">1,000,000+ 毕业生就业跟踪</div>
                    <div className="text-gray-600">数据规模</div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">核心发现：</h4>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">同班同学起薪相同7年后薪资竟然相差23倍</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">校友网络对个人量化价值差距高达450万+</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">传统优势专业与新兴专业的真实就业对比</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-purple-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">特朗普再度上台后获批工签最新行业分布</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <img 
                      src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/1a1715fff3db5312f7f9fd62f7acc7ba.jfif" 
                      alt="毕业后职业发展大数据" 
                      className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 服务优势 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">服务优势</h2>
            <p className="text-xl text-gray-600">专业团队，全程服务，个性化提升</p>
          </div>

          {/* 1. 擅长处理升学问题 */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-gray-900 mb-12 text-center">擅长处理升学问题</h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/1e419d073982024dda88d0edaec84ef6.jpg" 
                    alt="成绩不够好" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-blue-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-bar-chart-line mr-1 md:mr-2"></i>成绩不够好
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-blue-600 transition-colors">成绩不够好</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-blue-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">高考专科分，升入全球前100本科</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-blue-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">大学平均分60，进入香港前三</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Language%20learning%20breakthrough%20with%20TOEFL%20IELTS%20score%20improvement%20charts%2C%20student%20celebrating%20language%20test%20success%20with%20score%20progression%20from%20low%20to%20high%2C%20modern%20language%20learning%20center%20environment&width=400&height=250&seq=language-improvement-1&orientation=landscape" 
                    alt="语言分数低" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-green-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-translate-line mr-1 md:mr-2"></i>语言分数低
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-green-600 transition-colors">语言分数低</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-green-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">托福33分，3个月升至102</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-green-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">雅思3.5，5周考出6.5</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Career%20transformation%20from%20traditional%20majors%20to%20high-demand%20fields%2C%20professional%20showing%20career%20pivot%20from%20civil%20engineering%20to%20AI%20technology%2C%20modern%20tech%20workspace%20with%20career%20transition%20success%20story&width=400&height=250&seq=career-transformation-1&orientation=landscape" 
                    alt="难就业专业" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-purple-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-exchange-line mr-1 md:mr-2"></i>难就业专业
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-purple-600 transition-colors">难就业专业</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-purple-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">土木工程转人工智能名校</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-purple-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">文科生拿到医学名校offer</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Ivy%20League%20university%20acceptance%20letter%20with%20prestigious%20campus%20background%2C%20student%20from%20ordinary%20university%20celebrating%20admission%20to%20top-tier%20institution%2C%20academic%20achievement%20transformation%20story&width=400&height=250&seq=school-background-1&orientation=landscape" 
                    alt="背景学校差" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-orange-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-school-line mr-1 md:mr-2"></i>背景学校差
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-orange-600 transition-colors">背景学校差</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-orange-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">双非本科斩获常春藤offer</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-orange-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">专升硕再读博，进入全球前20</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Educational%20comeback%20story%20with%20student%20returning%20to%20studies%20after%20gap%20year%2C%20prestigious%20university%20campus%20with%20fresh%20start%20and%20second%20chance%20success%2C%20academic%20resilience%20and%20determination&width=400&height=250&seq=comeback-story-1&orientation=landscape" 
                    alt="休学或辍学" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-red-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-restart-line mr-1 md:mr-2"></i>休学或辍学
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-red-600 transition-colors">休学或辍学</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-red-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">高中休学1年半，录取英国名校</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-red-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">大学退学，转入美国前十名校</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Mature%20student%20returning%20to%20education%20after%20years%20away%2C%20business%20school%20campus%20with%20adult%20learner%20success%20story%2C%20career%20change%20and%20educational%20pursuit%20later%20in%20life&width=400&height=250&seq=return-education-1&orientation=landscape" 
                    alt="离校多年后" 
                    className="w-full h-32 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-teal-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                    <i className="ri-time-line mr-1 md:mr-2"></i>离校多年后
                  </div>
                </div>
                <div className="p-3 md:p-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-teal-600 transition-colors">离校多年后</h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">网红低学历背景，录取亚洲第一大学</span>
                    </div>
                    <div className="flex items-start space-x-2 md:space-x-3">
                      <div className="w-4 h-4 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs md:text-sm leading-relaxed">躺平无业多年后，进入全球第7商学院</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. 全流程一站式服务 */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-gray-900 mb-12 text-center">全流程一站式服务</h3>
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">留学咨询服务流程图</h4>
                <p className="text-lg text-gray-600">前沿教育Frontiers的申请涵盖四大服务阶段，十五个关键申请服务节点</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <img 
                  src="https://static.readdy.ai/image/369c6d7d3639e005f7b761af84a5109d/129e5a76ae0b21e2f699d3ff3776e258.png" 
                  alt="留学咨询服务流程图" 
                  className="w-full h-auto object-cover object-top rounded-2xl"
                />
              </div>
              <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="text-center bg-blue-50 rounded-2xl p-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-lightbulb-line text-white text-2xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-gray-900 mb-3">规划期</h5>
                  <p className="text-gray-600 text-sm leading-relaxed">前期评估与专业规划，制定个性化留学方案</p>
                </div>
                <div className="text-center bg-green-50 rounded-2xl p-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-tools-line text-white text-2xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-gray-900 mb-3">执行期</h5>
                  <p className="text-gray-600 text-sm leading-relaxed">背景提升与材料准备，全面增强申请竞争力</p>
                </div>
                <div className="text-center bg-purple-50 rounded-2xl p-6">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-send-plane-line text-white text-2xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-gray-900 mb-3">申请期</h5>
                  <p className="text-gray-600 text-sm leading-relaxed">文书创作与申请提交，专业团队全程跟进</p>
                </div>
                <div className="text-center bg-orange-50 rounded-2xl p-6">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-trophy-line text-white text-2xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-gray-900 mb-3">签证期</h5>
                  <p className="text-gray-600 text-sm leading-relaxed">录取跟进与签证办理，确保顺利入学</p>
                </div>
              </div>
            </div>
          </div>

          {/* 3. 个性化服务提升 - 增值服务项目 */}
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-12 text-center">个性化服务提升</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-blue-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-microscope-line text-blue-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">科研项目内推</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">对接名校教授科研项目，提升学术背景</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-blue-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">教授资源匹配</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-blue-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">项目申请指导</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-blue-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">研究过程支持</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-blue-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">成果发表协助</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">6-12个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-blue-600 font-bold text-lg">¥38,800起</span>
                  </div>
                  <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-green-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-briefcase-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">名企实习内推</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">顶尖名企实习机会，丰富职业经历</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">企业资源对接</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">简历包装优化</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">面试技巧培训</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">入职指导支持</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">1-3个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-green-600 font-bold text-lg">¥12,800起</span>
                  </div>
                  <button className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-purple-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-team-line text-purple-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">社会活动规划</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">规划有意义的社会实践活动，丰富个人履历</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-purple-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">活动策划指导</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-purple-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">志愿服务安排</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-purple-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">社区参与项目</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-purple-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">领导力培养</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">3-6个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-purple-600 font-bold text-lg">¥15,800起</span>
                  </div>
                  <button className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-orange-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-trophy-line text-orange-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">知名竞赛辅导</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">专业指导各类学术竞赛，提升竞争优势</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-orange-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">竞赛选择建议</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-orange-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">备赛策略制定</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-orange-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">专业导师辅导</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-orange-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">获奖概率提升</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">2-4个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-orange-600 font-bold text-lg">¥28,800起</span>
                  </div>
                  <button className="w-full bg-orange-600 text-white py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-red-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-school-line text-red-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">常青藤夏校申请</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">申请顶级夏校项目，提前体验名校生活</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-red-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">夏校项目筛选</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-red-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">申请材料指导</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-red-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">面试准备培训</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-red-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">录取跟进服务</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">2-3个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-red-600 font-bold text-lg">¥22,800起</span>
                  </div>
                  <button className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 cursor-pointer group">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-palette-line text-indigo-600 text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">作品集指导</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">针对艺术设计类专业的作品集创作指导</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-indigo-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">作品主题规划</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-indigo-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">创作技巧指导</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-indigo-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">排版设计优化</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-indigo-600 text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">院校要求匹配</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-500 text-sm">服务周期</span>
                    <span className="text-gray-900 font-medium">4-6个月</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-500 text-sm">服务费用</span>
                    <span className="text-indigo-600 font-bold text-lg">¥35,800起</span>
                  </div>
                  <button className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
                    了解详情
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 免费评估表单 */}
      <section id="assessment-section" className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">免费评估</h2>
            <p className="text-xl text-gray-300">填写信息，获取专业的留学申请评估和建议</p>
          </div>
          
          <form 
            id="assessment-form" 
            className="bg-white rounded-2xl p-8 text-gray-900"
            onSubmit={handleSubmit}
            data-readdy-form
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  姓名 *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="请输入您的姓名"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  联系电话 *
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="请输入您的手机号码"
                />
              </div>
              
              <div>
                <label htmlFor="wechat" className="block text-sm font-medium text-gray-700 mb-2">
                  微信 *
                </label>
                <input
                  id="wechat"
                  name="wechat"
                  type="text"
                  required
                  value={formData.wechat}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="请输入您的微信号"
                />
              </div>
              
              <div>
                <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                  当前学历
                </label>
                <select
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">请选择当前学历</option>
                  <option value="高中在读">高中在读</option>
                  <option value="高中毕业">高中毕业</option>
                  <option value="本科在读">本科在读</option>
                  <option value="本科毕业">本科毕业</option>
                  <option value="硕士在读">硕士在读</option>
                  <option value="硕士毕业">硕士毕业</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="problems" className="block text-sm font-medium text-gray-700 mb-2">
                  目前面临的难题和困惑
                </label>
                <textarea
                  id="problems"
                  name="problems"
                  rows={4}
                  maxLength={500}
                  value={formData.problems}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  placeholder="请简述您目前面临的留学申请难题和困惑（限500字）"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {charCount}/500
                </div>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '提交中...' : '获取免费评估'}
            </button>
            
            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                提交成功！我们将尽快与您联系。
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                提交失败，请稍后重试。
              </div>
            )}
            
            <p className="text-center text-sm text-gray-500 mt-4">
              提交即表示您同意我们的隐私政策，我们将严格保护您的个人信息
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default StudyPage;
